import React, { useEffect, useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import 'react-datepicker/dist/react-datepicker.css';
import SummaryApi from '../common';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import displayINRCurrency from '../helpers/displayCurrency';
import { useReactTable, flexRender, getCoreRowModel } from '@tanstack/react-table';

const AllOrders = () => {
  const [orderData, setOrderData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all'); 
  const navigate = useNavigate();

  // 1. Fetch Orders
  const fetchData = async () => {
    setLoading(true);
    try {
      let url = SummaryApi.allOrder.url;
      if (searchTerm) {
        url += `?search=${encodeURIComponent(searchTerm)}`;
      }

      const response = await fetch(url, {
        method: SummaryApi.allOrder.method,
        headers: {
          Authorization: SummaryApi.bearerToken,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error(`Failed: ${response.status}`);
      const result = await response.json();

      if (Array.isArray(result.data)) {
        setOrderData(result.data.reverse());
      } else if (Array.isArray(result)) {
        setOrderData(result.reverse());
      } else {
        setOrderData([]);
      }
    } catch (err) {
      toast.error(err.message);
      setOrderData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [searchTerm]);

  // 2. Handle Status Update
  const handleUpdateStatus = async (orderId, newStatus) => {
    try {
      const response = await fetch(SummaryApi.editOrderStatus.url, {
        method: SummaryApi.editOrderStatus.method,
        headers: {
          Authorization: SummaryApi.bearerToken,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          order_id: orderId,
          order_status: newStatus,
        }),
      });

      const data = await response.json();
      if (response.ok) {
        toast.success(data.message || "Status updated successfully");
        setOrderData((prev) =>
          prev.map((order) =>
            order.order_id === orderId ? { ...order, order_status: newStatus } : order
          )
        );
      } else {
        toast.error(data.message || "Failed to update status");
      }
    } catch (error) {
      console.error("Error updating status:", error);
    }
  };

  // 3. Handle Navigation (The Fix)
  const handleEdit = (order) => {
    // This constructs the full path: /admin-panel/orders/edit/123
    // We also pass 'state' so the data is available immediately without re-fetching
    navigate(`/admin-panel/orders/edit/${order.order_id}`, { state: { orderData: order } });
  };

  // --- Filtering Logic ---
  const filteredOrders = useMemo(() => {
    let data = [...orderData];
    
    // Date ranges logic
    const today = new Date();
    let start, end;
    
    if (filterType === 'today') {
        start = new Date(today.setHours(0,0,0,0));
        end = new Date(today.setHours(23,59,59,999));
    } else if (filterType === 'weekly') {
        start = new Date(today);
        start.setDate(today.getDate() - 6);
        start.setHours(0,0,0,0);
        end = new Date(today.setHours(23,59,59,999));
    } else if (filterType === 'monthly') {
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59);
    }

    if (start && end) {
        data = data.filter(o => {
            const d = new Date(o.order_date || o.created_date);
            return d >= start && d <= end;
        });
    }

    // Search Logic
    if (searchTerm) {
        const s = searchTerm.toLowerCase();
        data = data.filter(o => 
            o.order_code?.toString().toLowerCase().includes(s) ||
            o.customer_name?.toLowerCase().includes(s) ||
            o.mobile_number?.includes(searchTerm)
        );
    }
    return data;
  }, [orderData, filterType, searchTerm]);


  // --- Table Columns (Wrapped in useMemo) ---
  const columns = useMemo(() => [
    {
      header: 'S.No',
      id: 'sno',
      cell: ({ row }) => <span>{row.index + 1}</span>,
    },
    {
      header: 'Order Code',
      accessorKey: 'order_code',
    },
    {
      header: 'Customer Name',
      accessorKey: 'customer_name',
    },
    {
      header: 'Mobile',
      accessorKey: 'mobile_number',
    },
    {
      header: 'Total',
      accessorKey: 'total_amount',
      cell: ({ getValue }) => displayINRCurrency(parseFloat(getValue() || 0)),
    },
    {
      header: 'Order Date',
      accessorKey: 'order_date',
      cell: ({ getValue }) => {
        const date = getValue() ? new Date(getValue()) : null;
        return date ? date.toLocaleDateString('en-IN') : 'N/A';
      },
    },
    {
      header: 'Order Status',
      accessorKey: 'order_status'
    },
  
  {
    header: 'Order Status',
    accessorKey: 'order_status',
    cell: ({ row }) => {
      const status = row.original.order_status.toLowerCase();

      // Define colors for each status
      const statusStyles = {
        pending: { backgroundColor: '#FFF3CD', color: '#856404' },         // yellow
        'order placed': { backgroundColor: '#D1ECF1', color: '#0C5460' }, // light blue
        processing: { backgroundColor: '#CCE5FF', color: '#004085' },      // blue
        'order confirmed': { backgroundColor: '#C3E6CB', color: '#155724' }, // green
        'order in transit': { backgroundColor: '#E2E3E5', color: '#383D41' }, // grey
        delivered: { backgroundColor: '#D4EDDA', color: '#155724' },       // darker green
        cancelled: { backgroundColor: '#F8D7DA', color: '#721C24' },       // red
      };


      const style = statusStyles[status] || {};

      return (
        <span style={{ padding: '4px 8px', borderRadius: '4px', ...style }}>
          {row.original.order_status}
        </span>
      );
    },
  },


    {
      header: 'Action',
      cell: ({ row }) => (
        <button
          onClick={() => handleEdit(row.original)}
          className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
        >
          View
        </button>
      ),
    },
  ], []); // Empty dependency array for columns ensures they don't re-render unnecessarily

  const table = useReactTable({
    data: filteredOrders,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const downloadExcel = () => {
    const ws = XLSX.utils.json_to_sheet(filteredOrders);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Orders');
    XLSX.writeFile(wb, 'Orders.xlsx');
  };

  return (
    <div className="bg-white p-4 md:p-6 rounded-lg shadow-lg border">
      <div className='flex justify-between items-center mb-4'>
        <h2 className="text-xl font-semibold">Order Details</h2>
        <input 
            type="text" 
            placeholder="Search..." 
            className="border p-2 rounded text-sm"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="flex gap-2 mb-4">
        {['all', 'today', 'weekly', 'monthly'].map((type) => (
            <button 
                key={type}
                onClick={() => setFilterType(type)} 
                className={`px-3 py-1 rounded capitalize text-sm ${filterType === type ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}
            >
                {type}
            </button>
        ))}
      </div>

      <div className="overflow-x-auto border rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id} className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase">
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
                <tr><td colSpan={columns.length} className="text-center py-4">Loading...</td></tr>
            ) : table.getRowModel().rows.map((row) => (
              <tr key={row.id} className="hover:bg-gray-50">
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="px-6 py-4 text-sm whitespace-nowrap">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AllOrders;